import { useEffect, useRef, useState } from 'react';

/** Khớp media query CSS (bố cục đáp ứng W1.4), cập nhật khi đổi kích thước cửa sổ. Trình duyệt không có matchMedia → false. */
export function useMediaQuery(query: string): boolean {
  const get = () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/**
 * Esc đóng lớp phủ không phải hộp thoại (ngăn kéo, khung toàn màn) khi `active`. Bỏ qua khi đang có hộp thoại
 * (`aria-modal`) — Esc khi đó thuộc về hộp thoại (Modal tự đóng và chặn lan truyền).
 */
export function useEscape(active: boolean, onEscape: () => void) {
  const handler = useRef(onEscape);
  handler.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      handler.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Giữ focus trong `box` (hộp thoại, ngăn kéo): Tab ở phần tử cuối về phần tử đầu, Shift+Tab ở đầu (hoặc chính khung)
 * về cuối. Gọi trong onKeyDown của khung; trả true khi đã chặn phím.
 */
export function trapTab(e: { key: string; shiftKey: boolean; preventDefault(): void }, box: HTMLElement | null): boolean {
  if (e.key !== 'Tab' || !box) return false;
  const items = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => n.getClientRects().length > 0);
  if (!items.length) return false;
  const first = items[0]!;
  const last = items.at(-1)!;
  const active = document.activeElement;
  if (e.shiftKey && (active === first || active === box)) {
    e.preventDefault();
    last.focus();
    return true;
  }
  if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
    return true;
  }
  return false;
}
